(ns app.front.view.admin.approver-groups
  (:require [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.session :as session]
            [core.util.util :as util]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-organizations
  (fn [_ _]
    {:comm/command {:command    :admin/approver-groups
                    :method     :get
                    :on-success [::entities/merge-entity-db]}}))

;; --------------------------
;; Dialog events ands subs
;; --------------------------

(rf/reg-event-db ::dialog-open
  (fn [db [_ k data]]
    (assoc db k (assoc data
                       :open? true
                       :disabled? false))))

(rf/reg-event-db ::dialog-close
  (fn [db [_ k]]
    (dissoc db k)))

(rf/reg-sub ::dialog-data
  (fn [db [_ k field]]
    (get-in db [k field])))

(rf/reg-event-fx ::dialog-edit
  (fn [{db :db} [_ k field v]]
    {:db (assoc-in db [k field] (cond-> v
                                  (string? v) str/trim))}))

(rf/reg-event-fx ::dialog-server-error
  (fn [_ [_ k error]]
    {:dispatch-n [[::dialog-edit k :disabled? false]
                  [:notifications/add :toast :error
                   (get comm/http-status->error-title error :error/error)
                   (get comm/http-status->error-description error :error/unknown)]]}))

(rc/reg-chain ::dialog-save
  (fn [_ [_ k]]
    (>evt [::dialog-edit k :disabled? true]))
  (fn [{:keys [db]} [_ k]]
    (case k
      ::new-group (let [{:keys [group-domain domain-name domain-domain
                                group-name domain-id]}                 (::new-group db)]
                    {:comm/command {:command         :admin/create-approver-group
                                    :data            (merge {:group-name group-name}
                                                            (if (= group-domain "new-domain")
                                                              {:domain-id     domain-id
                                                               :domain-name   domain-name
                                                               :domain-domain domain-domain}
                                                              {:domain (futil/parse-int group-domain)}))
                                    :on-server-error [::dialog-server-error k]}})
      ::add-admin (let [{:keys [group-id email first-name last-name user-groups]} (::add-admin db)]
                    {:comm/command {:command         :admin/add-user
                                    :data            {:parent-group-id group-id
                                                      :user/email      email
                                                      :user/first-name first-name
                                                      :user/last-name  last-name
                                                      :user/groups     (seq user-groups)}
                                    :on-server-error [::dialog-server-error k]}})))

  (fn [{:keys [db]} [_ k _ {:keys [result id]}]]
    ;; If the user is already member of any admin's sibling groups, :group/add-user does not do
    ;; anything. Fortunately we receive the user id on those cases, so we can add the admin group in
    ;; an alternative way.
    {:dispatch-n [(if (= result :existing)
                    (let [{:keys [admin-group-id]} (::add-admin db)]
                      [::add-admin admin-group-id id])
                    [::fetch-organizations])
                  [::dialog-close k]
                  (when (and (= k ::add-admin)
                             (= (-> db ::add-admin :email) (-> db :session/user :user/email)))
                    [::session/restore-session])]}))

(rf/reg-sub ::dialog-filled?
  (fn [db [_ k]]
    (case k
      ::new-group (let [{:keys [group-domain domain-name domain-domain
                                group-name domain-id]}                 (::new-group db)]
                    (and (not (str/blank? group-name))
                         (or (not= group-domain "new-domain")
                             (not-any? str/blank? [domain-id domain-name domain-domain]))))
      ::add-admin (let [{:keys [email first-name last-name user-groups]} (::add-admin db)]
                    (and (seq user-groups)
                         (->> [email first-name last-name]
                              (not-any? str/blank?)))))))


;; --------------------------------
;; Approver group events and subs
;; --------------------------------

(defn- domain-items [domains]
  (->> domains
       (map-indexed (fn [i d]
                      {:text  (:group/name d)
                       :key   (str "domain-" i)
                       :value (:db/id d)}))
       (sort-by :text)))

(rf/reg-sub ::default-group-item
  :<- [::domain-group-items]
  (fn [group-items]
    (-> group-items first :value)))

(rf/reg-event-fx ::new-group-open
  (fn [_ [_ selected-domain]]
    (>evt [::dialog-open ::new-group {:group-domain selected-domain
                                      :group-name   ""}])))

(rc/reg-chain ::rename-group
  (fn [_ [_ group-id new-name]]
    {:comm/command {:command :admin/rename-group
                    :data    {:group-id group-id
                              :new-name new-name}}})
  (fn [_ [_ group-id new-name]]
    (>evt [::entities/update-entity group-id {:group/name new-name}])))

(rf/reg-sub
  ::editing-group?
  (fn [db [_ group-id]]
    (get-in db [::editing-group group-id])))

(rf/reg-event-db
  ::toggle-editing-group
  (fn [db [_ group-id editing?]]
    (assoc-in db [::editing-group group-id] (boolean editing?))))

;; --------------------------
;; Admin events ands subs
;; --------------------------

(rf/reg-event-fx
  ::add-admin-open
  (fn [_ [_ group-id admin-group-id approver-group-id]]
    (>evt [::dialog-open ::add-admin (util/mmap {:email       ""
                                                 :first-name  ""
                                                 :last-name   ""
                                                 :user-groups #{admin-group-id}}
                                                group-id
                                                admin-group-id
                                                approver-group-id)])))

(rf/reg-event-fx ::remove-approver-group
  (fn [_ [_ group-id]]
    {:comm/command {:command    :admin.circulation-group/remove-group
                    :data       (util/mmap group-id)
                    :notify     {:success "Group removed"}
                    :on-success [::remove-group.success group-id]}}))

(rf/reg-event-fx ::remove-group.success
  (fn [_ [_ group-id status resp]]
    {:fx [[:dispatch [::entities/apply-changes status resp]]
          [:dispatch [::entities/remove-entity group-id]]
          [:dispatch [::fetch-organizations]]]}))

(rf/reg-event-fx ::remove-domain-group
  (fn [_ [_ group-id]]
    {:comm/command {:command    :admin.circulation-group/remove-domain-group
                    :data       (util/mmap group-id)
                    :notify     {:success "Group removed"}
                    :on-success [::remove-group.success group-id]}}))

(rc/reg-chain ::remove-admin
  (fn [_ [_ _ admin-group-id user-id]]
    {:comm/command {:command :group/edit-user-groups
                    :data    {:remove  [admin-group-id]
                              :add     []
                              :user-id user-id}}})
  (fn [{:keys [db]} [_ _ _ user-id]]
    {:dispatch-n [[::entities/remove-entity user-id]
                  [::fetch-organizations]
                  (when (= user-id (-> db :session/user :db/id))
                    [::session/restore-session])]}))

(rf/reg-event-fx ::add-admin
  (fn [_ [_ admin-group-id user-id]]
    {:comm/command {:command    :group/edit-user-groups
                    :data       {:remove  []
                                 :add     [admin-group-id]
                                 :user-id user-id}
                    :on-success [::fetch-organizations]}}))

(rf/reg-sub ::domain-groups
  :<- [::entities/entities (filter (comp #{:group.subtype/local-government
                                           :group.subtype/approver-organization}
                                         :group/subtype))]
  (fn [domain-groups _]
    (->> domain-groups
         (remove #(-> % :group/id (= "cloudpermit"))))))

(rf/reg-sub ::loading?
  :<- [::domain-groups]
  :-> empty?)

(rf/reg-sub ::domain-group-items
  :<- [::domain-groups]
  :-> domain-items)

;; --------------------------
;; Dialogs and modal bodies
;; --------------------------

(defn- dialog
  "Dialog with Cancel and Save buttons. The actual contents (modal body)
  is given via arguments."
  [{:keys [tr] :as options} {:keys [key focus-selector body
                                    title]}]
  (let [open?     (boolean (<sub [::dialog-data key :open?]))
        disabled? (boolean (<sub [::dialog-data key :disabled?]))
        cancel-fn #(>evt [::dialog-close key])]
    [ui-modal/composed-input-modal
     {:open                   open?
      :selector-primary-focus focus-selector
      :on-close               cancel-fn}
     (cc/modal-header {:close-modal cancel-fn
                       :title       title})

     (when open?
       (cc/modal-body
         {:class-name "overflow-visible"}
         [body options]))
     (cc/modal-footer
       {}
       (cc/button {:kind     :secondary
                   :disabled disabled?
                   :on-click cancel-fn}
                  (tr [:cancel]))
       (cc/button {:kind     :primary
                   :disabled (or disabled?
                                 (not (<sub [::dialog-filled? key])))
                   :on-click #(>evt [::dialog-save key])}
                  (tr [:save])))]))

(defn new-group-modal-body [{:keys [tr]}]
  (let [domains   (<sub [::domain-group-items])
        disabled? (boolean (<sub [::dialog-data ::new-group :disabled?]))
        domain    (<sub [::dialog-data ::new-group :group-domain])
        edit-fn   (fn [k]
                    (fn [e]
                      (>evt  [::dialog-edit ::new-group k (.. e -target -value)])))]
    [:div.w-full.p-8
     [:div.bx--row
      [:div.bx--col-xs-12
       (cc/select {:id         "new-group-domain"
                   :label-text (tr [:admin/group-domain])
                   :class-name "full-width-select"
                   :value      (or domain "")
                   :on-change  (edit-fn :group-domain)
                   :disabled   disabled?}
                  (for-frag [d (concat domains [{:text     ""
                                                 :key      ::-
                                                 :disabled true}
                                                {:text  (tr [:admin/new-domain])
                                                 :key   ::new-domain
                                                 :value "new-domain"}])]
                    (cc/select-item d)))]]
     (when (= "new-domain" domain)
       [:<>
        [:div.bx--row.mt-2.pl-6
         [:div.bx--col-xs-12
          [ui/text-input {:id         "domain-id"
                          :label-text (tr [:admin/domain-id])
                          :disabled   disabled?
                          :on-change  (edit-fn :domain-id)}]]
         [:div.bx--col-xs-12
          [ui/text-input {:id         "domain-name"
                          :label-text (tr [:admin/domain-name])
                          :disabled   disabled?
                          :on-change  (edit-fn :domain-name)}]]]
        [:div.bx--row.mt-2.pl-6
         [:div.bx--col-xs-12
          [ui/text-input {:id         "domain-domain"
                          :label-text (tr [:admin/domain-domain])
                          :disabled   disabled?
                          :on-change  (edit-fn :domain-domain)}]]]])
     [:div.bx--row.my-4
      [:div.bx--col-xs-12
       [ui/text-input {:id         "new-group-name"
                       :label-text (tr [:admin/group-name])
                       :disabled   disabled?
                       :on-change  (edit-fn :group-name)}]]]]))

(defn new-group-modal [{:keys [tr] :as options}]
  [dialog options {:key            ::new-group
                   :focus-selector "input#new-group-name"
                   :body           new-group-modal-body
                   :title          (tr [:admin/new-group])}])

(defn add-admin-modal-body [{:keys [tr]}]
  (let [disabled?    (boolean (<sub [::dialog-data ::add-admin :disabled?]))
        edit-fn      (fn [k]
                       (fn [e]
                         (>evt [::dialog-edit ::add-admin k (if (boolean? e)
                                                              e
                                                              (futil/tval e))])))
        group-id     (<sub [::dialog-data ::add-admin :group-id])
        user-groups  (set (<sub [::dialog-data ::add-admin :user-groups]))
        admin-gid    (<sub [::dialog-data ::add-admin :admin-group-id])
        approver-gid (<sub [::dialog-data ::add-admin :approver-group-id])]
    [:div.w-full
     [:div
      [:span (tr [:admin/add-admin-for]
                 [(:group/name (<sub [::entities/entity group-id]))])]
      [:span " "]
      [:span (tr [:user/add-user-help])]]
     [:div.flex.flex-wrap.-mx-2
      [:div.w-full.md:w-1-3.p-2
       [ui/text-input {:id            "add-admin-eml"
                       :type          "email"
                       :auto-complete "off"
                       :auto-focus    true
                       :label-text    (tr [:party/email])
                       :disabled      disabled?
                       :on-change     (edit-fn :email)}]]
      [:div.w-1-2.md:w-1-3.p-2
       [ui/text-input {:id            "add-admin-first-name"
                       :auto-complete "off"
                       :label-text    (tr [:party/first-name])
                       :disabled      disabled?
                       :on-change     (edit-fn :first-name)}]]
      [:div.w-1-2.md:w-1-3.p-2
       [ui/text-input {:id            "add-admin-last-name"
                       :auto-complete "off"
                       :label-text    (tr [:party/last-name])
                       :disabled      disabled?
                       :on-change     (edit-fn :last-name)}]]]
     [:div.flex.flex-wrap.gap-4.justify-start.mt-3
      [:div
       (cc/checkbox {:id         "add-as-admin"
                     :label-text "Administrator"
                     :checked    (contains? user-groups admin-gid)
                     :on-change  #(>evt [::dialog-edit
                                         ::add-admin
                                         :user-groups
                                         ((if % conj disj) user-groups admin-gid)])})]
      [:div
       (cc/checkbox {:id         "add-as-approver"
                     :label-text "Approver"
                     :checked    (contains? user-groups approver-gid)
                     :on-change  #(>evt [::dialog-edit
                                         ::add-admin
                                         :user-groups
                                         ((if % conj disj) user-groups approver-gid)])})]]]))

(defn add-admin-modal [{:keys [tr] :as options}]
  [dialog options {:key            ::add-admin
                   :focus-selector "input#add-admin-email"
                   :body           add-admin-modal-body
                   :title          (tr [:admin/add-admin])}])

;; -------------------------
;; Approver group table UI
;; -------------------------

(defn edit-button [{:keys [tr]} {:keys [group-id expand-fn expanded?]}]
  (let [editing? (<sub [::editing-group? group-id])]
    (when (and editing? (not expanded?))
      (>evt [::toggle-editing-group group-id (not editing?)]))
    [:div.flex.flex-1.justify-end
     (cc/button {:kind     (if editing? :secondary :primary)
                 :size     :small
                 :on-click (fn [_]
                             (>evt [::toggle-editing-group group-id (not editing?)])
                             (when (and (not editing?) (not expanded?))
                               (expand-fn)))}
                (tr [(if editing? :admin/stop-editing :admin/edit)]))]))

(defn group-details [{:keys [tr]} group-id]
  (let [editing?                (<sub [::editing-group? group-id])
        {group-name :group/name
         :as        group}      (<sub [::entities/entity group-id])
        {admin-group-id :db/id} (<sub [::entities/entity {:entity/parent group-id
                                                          :admin-group?  true}])
        {appr-group-id :db/id}  (<sub [::entities/entity {:entity/parent   group-id
                                                          :approver-group? true}])
        admins                  (<sub [::entities/entities
                                       (filter (fn [e]
                                                 (some-> e :user/groups set
                                                         (contains? admin-group-id))))])]
    [:div
     [:div.bx--row
      [:div.pr-8.pb-4.bx--col-xs-12.bx--col-lg-6
       (if editing?
         [ui/text-input {:id            (str "name-" group-id)
                         :label-text    (tr [:admin/approver-group-name])
                         :default-value group-name
                         :on-blur       (fn [e]
                                          (some->> (.. e -target -value)
                                                   str/trim
                                                   not-empty
                                                   (conj [::rename-group group-id])
                                                   >evt))}]
         [:<>
          [:div.bx--label (tr [:admin/approver-group-name])]
          [:div.label-value group-name]])]
      ;; TODO allow changing domain group?
      [:div.bx--col-xs-12.bx--col-lg-6
       [:div.bx--label (tr [:admin/group-admins])]
       [:div.table
        (for-frag [{:keys [id fullname
                           email]} (->> admins
                                        (map (fn [{id         :db/id
                                                   email      :user/email
                                                   first-name :user/first-name
                                                   last-name  :user/last-name}]
                                               {:id       id
                                                :email    email
                                                :fullname (str first-name " " last-name)}))
                                        (sort-by :fullname))]
          [:div.table-row {:key id}
           [:div.table-cell.align-middle {:key (str "fullname-" id)} fullname]
           [:div.table-cell.align-middle.px-4 {:key (str "email-" id)} email]
           (when editing?
             [:div.table-cell.align-middle {:key (str "remove-" id)}
              [ui/modal-confirmation-button
               {:tr                tr
                :id                "remove-admin"
                :trigger-icon-opts {:icon        i/remove
                                    :class-name  "cursor-pointer"
                                    :fill        "#cc1b00"
                                    :description (tr [:admin/remove-admin])}
                :on-yes-click      #(>evt [::remove-admin group-id admin-group-id id])}
               [:div (tr [:admin/remove-admin-confirmation] [fullname email group-name])]]])])]]]

     [:div.bx--row.mt-4
      [:div.bx--col-xs-12.bx--offset-lg-6.bx--col-lg-6
       (when editing?
         [:div.flex.flex-wrap
          [:div.mr-2
           [ui/modal-confirmation-button
            (util/mmap {:trigger-button-opts {:kind :danger
                                              :size :small
                                              :icon i/trash}
                        :disabled?           (or (->> group :group/_approver-groups seq some?)
                                                 (-> admins seq some?))
                        :on-yes-click        #(>evt [::remove-approver-group (:db/id group)])
                        :trigger-button-text "Delete group"}
                       tr)
            [:span
             "Are you sure you want to remove the approver group " [:span.bold (:group/name group)]
             "? If the group has any users or is assigned any circulations, it cannot be removed."]]]
          (cc/button {:kind             :primary
                      :size             :small
                      :icon             i/user-plus
                      :icon-description (tr [:admin/add-admin])
                      :on-click         #(>evt [::add-admin-open group-id admin-group-id appr-group-id])}
                     (tr [:admin/add-admin]))])]]]))

(defn render-groups [{:keys [tr]} props]
  (let [{:keys [rows headers getHeaderProps getRowProps expandRow]} (js->clj props :keywordize-keys true)]
    (r/as-element
      (cc/table-container
        {:class-name "w-full"}
        (cc/table
          {}
          (cc/table-head
            {}
            (cc/table-row
              {}
              (cc/table-expand-header)
              (for-frag [header headers
                         :let   [header-props (js->clj (getHeaderProps #js {:header   (clj->js header)
                                                                            :children (:header header)})
                                                       :keywordize-keys true)]]
                (cc/table-header header-props))))
          (cc/table-body
            {}
            (for-frag [row  rows
                       :let [row-id    (:id row)
                             group-id  (futil/parse-int row-id)
                             row-props (js->clj (getRowProps #js {:row                   (clj->js row)
                                                                  :expandIconDescription (tr [:carbon.table.row.expand])})
                                                :keywordize-keys true)
                             expanded? (:isExpanded row)]]
              [:<>
               (cc/table-expand-row
                 row-props
                 (for-frag  [cell (:cells row)
                             :let [header  (-> cell :info :header keyword)]]

                   (cc/table-cell
                     {:key (:id cell)}
                     (if (= header :edit)
                       [edit-button (util/mmap tr) {:group-id  group-id
                                                    :expand-fn #(expandRow row-id)
                                                    :expanded? expanded?}]
                       (:value cell)))))
               (when expanded?
                 (cc/table-expanded-row {:key      (str "expanded-" row-id)
                                         :col-span (inc (count headers))}
                                        [group-details (util/mmap tr) group-id]))])))))))

(defn stakeholder-groups [{:keys [tr] :as options}]
  (let [groups (<sub [::entities/entities {:stakeholder? true}])]
    (if (<sub [::loading?])
      (cc/data-table-skeleton)
      (cc/data-table {:is-sortable false
                      :headers     [{:key :group-name :header (tr [:admin/approver-group-name])}
                                    {:key :domain-name :header (tr [:admin/domain-name])}
                                    {:key :used-by :header "Used by"}
                                    {:key :edit :header ""}]
                      :rows        (map (fn [g]
                                          {:id          (str (:db/id g))
                                           :group-name  [:a {:href       (href/href :view/admin-groups
                                                                                    {:group-id (:db/id g)})
                                                             :aria-label (str "Go to " (:group/name g))}
                                                         (:group/name g)]
                                           :domain-name (-> g :entity/parent :group/name)
                                           :used-by     (->> g
                                                             :group/_approver-groups
                                                             (map :group/name)
                                                             (str/join ", "))})
                                        groups)
                      :render      (partial render-groups options)}))))

(defn domain-remove-link [{:keys [tr]} group]
  (when-not (-> group :entity/_parent seq)
    [ui/modal-confirmation-button
     {:trigger-button-text "Remove"
      :tr                  tr
      :text-button?        true
      :on-yes-click        #(>evt [::remove-domain-group (:db/id group)])}
     [:div
      [:p "Are you sure you want to remove " [:span.bold (:group/name group)] "?"]
      [:p "Removing is only possible if there are no sub-groups (e.g. authority groups), users or tasks for that group."]]]))

(defn domain-groups [{:keys [tr]}]
  [:div
   [ui/help
    "Domain groups represent municipalities or organizations above a municipality, such as a county- or state-level organization. They can be removed when not in use anymore.

The remove link may be visible even for groups that cannot be removed - in such case, the operation will fail."]
   [ui-table/modern-responsive-table
    {:cols  [["Group name" :group/name]
             ["ID" :group/id]
             ["Domain name" :group/domain]
             ["" (partial domain-remove-link (util/mmap tr))]]
     :tr    tr
     :items (<sub [::domain-groups])
     :link  {:make-href   (fn [{:keys [:db/id]}]
                            (href/href :view/admin-groups {:group-id id}))
             :description "View group"}}]])

(defn add-button [{:keys [tr]}]
  (let [default-domain (<sub [::default-group-item])]
    (cc/button {:icon-position    :left
                :icon             i/plus
                :icon-description (tr [:admin/add-approver-group])
                :on-click         #(>evt [::new-group-open default-domain])}
               (tr [:admin/add-approver-group]))))

(defn view [match]
  (let [tr (<sub [:tr])]
    [acommon/admin-page (util/mmap {:title (tr [:view/admin.approver-groups])}
                                   match)
     [:div.flex.justify-end
      [add-button (util/mmap tr)]]
     [section/section
      "domain-groups-section"
      (util/mmap {:initially-collapsed? true}
                 tr)
      (tr [:circulation-admin/domain-groups])
      [domain-groups (util/mmap tr)]]
     [section/section
      "circulation-groups-section"
      (util/mmap {:initially-collapsed? false}
                 tr)
      (tr [:view/admin.approver-groups])
      [stakeholder-groups (util/mmap tr)]]
     [new-group-modal (util/mmap tr)]
     [add-admin-modal (util/mmap tr)]]))

(def route
  {:name        :view/admin.approver-groups
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch-organizations]))}]})
